import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CheckCircle2, Phone, ArrowRight, Home, BookOpen, Target } from "lucide-react";
import { readStashedConfirmation, clearStashedConfirmation } from "../lib/attribution";

const GTM_ID = "GTM-NWRD7KSX";
const GTM_INSTALL_FLAG = "__vv_gtm_installed";

/**
 * Idempotent GTM install for this route only. The container is deliberately
 * scoped to /thank-you so it never fires on peptide / therapy / library / admin
 * pages — protects Google Ads conversion accuracy.
 *
 * Uses the canonical GTM snippet (async script tag + <noscript> iframe). Both
 * are guarded by a window-level flag so React StrictMode's double mount and
 * back/forward navigation to /thank-you never double-install the container.
 */
function useGtmOnce() {
  // Intentional mount-only effect: GTM must install exactly once per full page
  // load. The guard is `window[GTM_INSTALL_FLAG]`, not React state, so this
  // effect has *by design* no dependencies. Do not add deps to satisfy a linter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (window[GTM_INSTALL_FLAG]) return;
    window[GTM_INSTALL_FLAG] = true;

    // Prime dataLayer with the gtm.start event exactly like the canonical snippet.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

    // Async gtm.js loader
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    s.dataset.vvGtm = "true";
    document.head.appendChild(s);

    // Non-JS fallback iframe injected at the top of <body>. Kept for accessibility
    // + non-JS crawlers; still gated to /thank-you only because this hook only
    // runs on that route.
    if (!document.querySelector(`iframe[data-vv-gtm-noscript]`)) {
      const ns = document.createElement("noscript");
      ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden" data-vv-gtm-noscript="true"></iframe>`;
      document.body.insertBefore(ns, document.body.firstChild);
    }
  }, []);
}

/**
 * Dedicated post-submission thank-you page (a genuine route, not a modal).
 *
 * - `noindex,nofollow` — must not be indexed and must not be listed in the sitemap.
 * - Reads the confirmation number ONCE from sessionStorage on first render (via useState),
 *   so a page refresh keeps rendering the confirmation but no lead is ever recreated.
 * - Pushes a `generate_lead_success` event to the GTM dataLayer without any PII.
 * - Installs Google Tag Manager container GTM-NWRD7KSX for Google Ads conversion tracking.
 */
export default function ThankYou() {
  // Snapshot the values on first render only. `useState` initializer runs once,
  // which prevents React StrictMode's double useEffect cleanup from wiping the
  // confirmation before the component gets a chance to display it.
  const [{ confirmation, formName }] = useState(() => readStashedConfirmation());
  const pushedRef = useRef(false);

  // Install GTM exactly once on this route.
  useGtmOnce();

  useEffect(() => {
    // Fire GTM/GA4 conversion — this dataLayer.push carries no PII or PHI.
    // Guarded with a ref so React StrictMode's dev-only double effect does not
    // enqueue two conversion events; matches production behavior exactly.
    if (pushedRef.current) return;
    pushedRef.current = true;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "generate_lead_success",
        form_name: formName || "website_lead_form",
      });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("[thank-you] dataLayer push failed", err);
    }
  }, [formName]);

  // Clear the confirmation number on unmount so a later navigation back to this
  // page (without a fresh submission) shows the generic thank-you without a
  // stale confirmation. Kept in its own effect so StrictMode's dev cleanup does
  // not fight the render — the useState snapshot above owns the display value.
  // Intentional mount-only cleanup; no deps by design.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => { clearStashedConfirmation(); }, []);

  return (
    <div
      id="lead-submission-thank-you"
      data-testid="thank-you-page"
      className="min-h-[70vh] grid place-items-center bg-stone-50 px-4 py-16"
    >
      <Helmet>
        <title>Thank You | Medical Peptides Powered by VidaVital Medical</title>
        <meta name="description" content="Your consultation request has been received. A VidaVital Medical care coordinator will contact you shortly." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="vv-card p-10 md:p-14 text-center max-w-2xl w-full">
        <CheckCircle2 className="text-[color:var(--vv-gold)] mx-auto" size={48} />
        <h1 className="font-display text-3xl md:text-4xl mt-4">Thank you — request received</h1>
        <p className="text-stone-600 mt-3 max-w-xl mx-auto">
          Your consultation request has been received successfully. A member of the VidaVital Medical
          team will contact you shortly. For immediate assistance, call{" "}
          <a href="tel:+18887738677" data-testid="thank-you-phone-inline" className="text-[color:var(--vv-gold-deep)] font-semibold">(888) 773-8677</a>.
        </p>
        <p className="text-stone-500 mt-2 text-sm max-w-xl mx-auto">
          In the meantime, continue exploring our educational resources.
        </p>

        {confirmation && (
          <div
            data-testid="thank-you-confirmation"
            className="mt-6 inline-block bg-amber-50 border border-amber-200 rounded-full px-5 py-2 text-xs uppercase tracking-widest text-amber-900"
          >
            Confirmation: <span className="font-semibold">{confirmation}</span>
          </div>
        )}

        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          <Link
            to="/"
            data-testid="thank-you-home-btn"
            className="vv-btn-outline inline-flex items-center justify-center gap-2"
          >
            <Home size={16} /> Return to Home
          </Link>
          <Link
            to="/peptide-library"
            data-testid="thank-you-library-btn"
            className="vv-btn-outline inline-flex items-center justify-center gap-2"
          >
            <BookOpen size={16} /> Peptide Library
          </Link>
          <Link
            to="/treatment-goals"
            data-testid="thank-you-goals-btn"
            className="vv-btn-outline inline-flex items-center justify-center gap-2"
          >
            <Target size={16} /> Health Goals
          </Link>
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <a
            href="tel:+18887738677"
            data-testid="thank-you-phone-btn"
            className="vv-btn-primary inline-flex items-center gap-2"
          >
            <Phone size={16} /> Call (888) 773-8677 <ArrowRight size={16} />
          </a>
        </div>

        <p className="mt-10 text-xs text-stone-500">
          Educational information only. Not medical advice. A licensed provider will independently
          review your request during your consultation.
        </p>
      </div>
    </div>
  );
}
